import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes } from "react-router";
import ActiveSubscriptionGuard from "./components/active-subscription-guard";
import BillingLayout from "./components/billing-layout";
import CanGuard from "./components/can-guard";
import FeatureFlagGuard from "./components/feature-flag-guard";
import ForceCompleteWizard from "./components/force-complete-wizard";
import HomeLayout from "./components/home-layout";
import Loader from "./components/loader";
import ManagerGuard from "./components/manager-guard";
import MeasurementCreationGuard from "./components/measurement/measurement-creation-guard";
import NavigationErrorBoundary from "./components/navigation-error-boundary";
import ProtectedGuard from "./components/protected-guard";
import PublicGuard from "./components/public-guard";
import SubscriptionExpiredGuard from "./components/subscription-expired-guard";
import UserSwitchHandler from "./components/user-switch-handler";
import WelcomeGuard from "./components/welcome-guard";
import {
  createIngredientPagePermissions,
  createTemplatePagePermissions,
  editIngredientsPagePermissions,
  editTemplatesPagePermissions,
  sharePagePermissions,
  templatePagePermissions,
  templatesPagePermissions,
  usersPagePermissions,
  viewBillingAll,
} from "./constants/page-permissions";
import routes, { refMutationRoute } from "./constants/routes";
import {
  createTemplatePagePermissions as createTemplatePagePermissionsV1,
  editNutritionPagePermissions as editNutritionPagePermissionsV1,
  editTemplateMealsPagePermissions as editTemplateMealsPagePermissionsV1,
  editTemplatesPagePermissions as editTemplatesPagePermissionsV1,
  templateMealsPagePermissions as templateMealsPagePermissionsV1,
  templatesPagePermissions as templatesPagePermissionsV1,
} from "./constants/v1/page-permissions";

const LoginPage = lazy(() => import("./login/page"));
const LogoutPage = lazy(() => import("./logout/page"));
const DashboardPage = lazy(() => import("./page"));
const BillingAllPage = lazy(
  () => import("./settings/billing/all/page-wrapper"),
);
const StepsPage = lazy(() => import("./steps/page"));
const StepsCreationPage = lazy(() => import("./steps/add/page"));
const StepsEditPage = lazy(() => import("./steps/edit/page"));
const StepsEditFormPage = lazy(() => import("./steps/edit/[id]/page"));
const UnauthorizedPage = lazy(() => import("./unauthorized/page"));
const NotFound = lazy(() => import("./not-found/page"));
const WelcomePage = lazy(() => import("./welcome/page"));
const FeatureFlagsPage = lazy(() => import("./feature-flags/page"));
const MeasurementsPage = lazy(() => import("./measurements/page"));
const WeightPage = lazy(() => import("./weight/page"));
const WeightСreationPage = lazy(() => import("./weight/add/page"));
const WeightEditPage = lazy(() => import("./weight/edit/page"));
const WeightEditFormPage = lazy(() => import("./weight/edit/[id]/page"));
const ShoulderPage = lazy(() => import("./shoulder/page"));
const ShoulderCreationPage = lazy(() => import("./shoulder/add/page"));
const ShoulderEditPage = lazy(() => import("./shoulder/edit/page"));
const ShoulderEditFormPage = lazy(() => import("./shoulder/edit/[id]/page"));
const ChestPage = lazy(() => import("./chest/page"));
const ChestCreationPage = lazy(() => import("./chest/add/page"));
const ChestEditPage = lazy(() => import("./chest/edit/page"));
const ChestEditFormPage = lazy(() => import("./chest/edit/[id]/page"));
const WaistPage = lazy(() => import("./waist/page"));
const WaistCreationPage = lazy(() => import("./waist/add/page"));
const WaistEditPage = lazy(() => import("./waist/edit/page"));
const WaistEditFormPage = lazy(() => import("./waist/edit/[id]/page"));
const HipsPage = lazy(() => import("./hips/page"));
const HipsCreationPage = lazy(() => import("./hips/add/page"));
const HipsEditPage = lazy(() => import("./hips/edit/page"));
const HipsEditFormPage = lazy(() => import("./hips/edit/[id]/page"));
const HipPage = lazy(() => import("./hip/page"));
const HipEditPage = lazy(() => import("./hip/edit/page"));
const HipEditFormPage = lazy(() => import("./hip/edit/[id]/page"));
const HipCreationPage = lazy(() => import("./hip/add/page"));
const ConditionalNutritionPage = lazy(
  () => import("./nutrition/conditional-nutrition-page"),
);
const NutritionEditPage = lazy(() => import("./nutrition-edit/page"));
const NutritionEditTemplatesPage = lazy(
  () => import("./nutrition-edit/templates/page"),
);
const NutritionEditTemplatesCreatePage = lazy(
  () => import("./nutrition-edit/templates/create/page"),
);
const NutritionEditTemplatesEditPage = lazy(
  () => import("./nutrition-edit/templates/edit/page"),
);
const NutritionEditTemplateEditPage = lazy(
  () => import("./nutrition-edit/templates/edit/[id]/page"),
);
const NutritionEditTemplateMealsPage = lazy(
  () => import("./nutrition-edit/templates/[id]/meals/page"),
);
const NutritionEditTemplateMealsEditPage = lazy(
  () => import("./nutrition-edit/templates/[id]/meals/edit/page"),
);
const ExercisePage = lazy(() => import("./exercise/page"));
const HomeExercisePage = lazy(() => import("./exercise/home/page"));
const GymExercisePage = lazy(() => import("./exercise/gym/page"));
const MainLayout = lazy(() => import("./components/main-layout"));
const NutritionLayout = lazy(() => import("./components/nutrition-layout"));
const NutritionEditTemplatesLayout = lazy(
  () => import("./nutrition-edit/templates/components/templates-layout"),
);
const NutritionEditTemplateMealsLayout = lazy(
  () =>
    import(
      "./nutrition-edit/templates/[id]/meals/components/template-meals-layout"
    ),
);
const NutritionEditEditTemplateLayout = lazy(
  () =>
    import(
      "./nutrition-edit/templates/edit/[id]/components/edit-template-layout"
    ),
);
const SettingsLayout = lazy(() => import("./components/settings-layout"));
const SettingsPage = lazy(() => import("./settings/page"));
const SystemSettingsPage = lazy(() => import("./settings/system/page"));
const ProfileSettingsPage = lazy(() => import("./settings/profile/page"));
const MedicalSettingsPage = lazy(() => import("./settings/medical/page"));
const ActivitySettingsPage = lazy(() => import("./settings/activity/page"));
const BillingSettingsPage = lazy(() => import("./settings/billing/page"));
const SharePage = lazy(() => import("./share/page"));
const RefMutation = lazy(() => import("./components/ref-mutation"));
const MealPageV1 = lazy(() => import("./nutrition/meal/page"));
const AddIngredientPageV1 = lazy(
  () => import("./nutrition/meal/add-ingredient/page"),
);
const IngredientsEditPageV1 = lazy(
  () => import("./nutrition/meal/ingredients-edit/page"),
);

const AddIngredientPage = lazy(() => import("./nutrition/v2/edit/add/page"));
const IngredientsEditPage = lazy(() => import("./nutrition/v2/edit/page"));
const SubscriptionExpiredPage = lazy(
  () => import("./subscription-expired/page"),
);

const ChooseUsersPage = lazy(() => import("./settings/users/page"));

const TemplatesPage = lazy(() => import("./nutrition/v2/edit/templates/page"));
const TemplatesLayout = lazy(
  () => import("./nutrition/v2/edit/templates/components/templates-layout"),
);
const TemplateCreationPage = lazy(
  () => import("./nutrition/v2/edit/templates/create/page"),
);
const EditTemplatesPage = lazy(
  () => import("./nutrition/v2/edit/templates/edit/page"),
);
const EditTemplateLayout = lazy(
  () =>
    import(
      "./nutrition/v2/edit/templates/edit/[id]/components/edit-template-layout"
    ),
);
const EditTemplatePage = lazy(
  () => import("./nutrition/v2/edit/templates/edit/[id]/page"),
);
const MeasurementLayout = lazy(
  () => import("./components/measurement/measurement-layout"),
);
const EditIngredientPage = lazy(() => import("./nutrition/v2/edit/[id]/page"));
const EditIngredientLayout = lazy(
  () => import("./nutrition/v2/edit/[id]/components/edit-ingredient-layout"),
);
const EditIngredientGuard = lazy(
  () => import("./nutrition/v2/edit/[id]/components/edit-ingredient-guard"),
);
const EditIngredientsLayout = lazy(
  () => import("./nutrition/v2/edit/components/edit-ingredients-layout"),
);
const TemplateLayout = lazy(
  () => import("./nutrition/v2/edit/templates/[id]/components/template-layout"),
);
const TemplatePage = lazy(
  () => import("./nutrition/v2/edit/templates/[id]/page"),
);
const AddTemplateIngredientPage = lazy(
  () => import("./nutrition/v2/edit/templates/[id]/add/page"),
);
const MealLayoutV1 = lazy(
  () => import("./nutrition/meal/components/meal-layout"),
);
const EditMealLayoutV1 = lazy(
  () => import("./nutrition/meal/ingredients-edit/components/edit-meal-layout"),
);
const EditIngredientsGuardV1 = lazy(
  () => import("./nutrition/meal/components/edit-ingredients-guard"),
);
const CreateIngredientsGuardV1 = lazy(
  () => import("./nutrition/meal/components/create-ingredients-guard"),
);

const RoutingSystem = () => {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/401" element={<UnauthorizedPage />} />
        <Route element={<PublicGuard />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>
        <Route
          element={
            <Suspense fallback={<Loader />}>
              <NavigationErrorBoundary>
                <ProtectedGuard>
                  <UserSwitchHandler>
                    <ForceCompleteWizard />
                  </UserSwitchHandler>
                </ProtectedGuard>
              </NavigationErrorBoundary>
            </Suspense>
          }
        >
          {routes.map(([path, Component]) => (
            <Route
              key={String(path)}
              element={
                <Suspense fallback={<Loader />}>
                  {path === refMutationRoute ? (
                    <RefMutation />
                  ) : (
                    <ManagerGuard />
                  )}
                </Suspense>
              }
            >
              <Route path={String(path)} element={<Component />} />
            </Route>
          ))}
          <Route
            element={
              <Suspense fallback={<Loader />}>
                <ManagerGuard>
                  <SubscriptionExpiredGuard>
                    <WelcomeGuard />
                  </SubscriptionExpiredGuard>
                </ManagerGuard>
              </Suspense>
            }
          >
            <Route
              path="/"
              element={
                <HomeLayout title="Головна">
                  <DashboardPage />
                </HomeLayout>
              }
            />
            <Route path="/steps">
              <Route
                index
                element={
                  <MeasurementLayout title="Кроки">
                    <StepsPage />
                  </MeasurementLayout>
                }
              />
              <Route
                element={<MeasurementCreationGuard type="steps" range={1} />}
              >
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису кроків" backTrigger>
                      <StepsCreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<StepsEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису кроків" backTrigger>
                      <StepsEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/logout" element={<LogoutPage />} />
            <Route path="/weight">
              <Route
                index
                element={
                  <MeasurementLayout title="Вага">
                    <WeightPage />
                  </MeasurementLayout>
                }
              />
              <Route element={<MeasurementCreationGuard type="weight" />}>
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису ваги" backTrigger>
                      <WeightСreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<WeightEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису ваги" backTrigger>
                      <WeightEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/shoulder">
              <Route
                index
                element={
                  <MeasurementLayout title="Обхват плеча">
                    <ShoulderPage />
                  </MeasurementLayout>
                }
              />
              <Route element={<MeasurementCreationGuard type="shoulder" />}>
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису плеча" backTrigger>
                      <ShoulderCreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<ShoulderEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису плеча" backTrigger>
                      <ShoulderEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/chest">
              <Route
                index
                element={
                  <MeasurementLayout title="Обхват грудей">
                    <ChestPage />
                  </MeasurementLayout>
                }
              />
              <Route element={<MeasurementCreationGuard type="chest" />}>
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису грудей" backTrigger>
                      <ChestCreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<ChestEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису грудей" backTrigger>
                      <ChestEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/waist">
              <Route
                index
                element={
                  <MeasurementLayout title="Обхват талії">
                    <WaistPage />
                  </MeasurementLayout>
                }
              />
              <Route element={<MeasurementCreationGuard type="waist" />}>
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису талії" backTrigger>
                      <WaistCreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<WaistEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису талії" backTrigger>
                      <WaistEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/hips">
              <Route
                index
                element={
                  <MeasurementLayout title="Обхват стегон">
                    <HipsPage />
                  </MeasurementLayout>
                }
              />
              <Route element={<MeasurementCreationGuard type="hips" />}>
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису стегон" backTrigger>
                      <HipsCreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<HipsEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису стегон" backTrigger>
                      <HipsEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/hip">
              <Route
                index
                element={
                  <MeasurementLayout title="Обхват стегна">
                    <HipPage />
                  </MeasurementLayout>
                }
              />
              <Route element={<MeasurementCreationGuard type="hip" />}>
                <Route
                  path="add"
                  element={
                    <MainLayout title="Створення запису стегна" backTrigger>
                      <HipCreationPage />
                    </MainLayout>
                  }
                />
              </Route>
              <Route path="edit">
                <Route index element={<HipEditPage />} />
                <Route
                  path=":id"
                  element={
                    <MainLayout title="Редагування запису стегна" backTrigger>
                      <HipEditFormPage />
                    </MainLayout>
                  }
                />
              </Route>
            </Route>
            <Route path="/nutrition">
              <Route
                index
                element={
                  <NutritionLayout>
                    <ConditionalNutritionPage />
                  </NutritionLayout>
                }
              />
              <Route element={<FeatureFlagGuard featureFlag="ffMealsV2" />}>
                <Route
                  path="edit"
                  element={
                    <CanGuard permission={editIngredientsPagePermissions} />
                  }
                >
                  <Route
                    index
                    element={
                      <EditIngredientsLayout>
                        <IngredientsEditPage />
                      </EditIngredientsLayout>
                    }
                  />
                  <Route
                    element={
                      <CanGuard permission={createIngredientPagePermissions} />
                    }
                  >
                    <Route
                      path="add"
                      element={
                        <MainLayout title="Додати інгредієнт" backTrigger>
                          <AddIngredientPage />
                        </MainLayout>
                      }
                    />
                  </Route>
                  <Route
                    path="templates"
                    element={<CanGuard permission={templatesPagePermissions} />}
                  >
                    <Route
                      index
                      element={
                        <TemplatesLayout>
                          <TemplatesPage />
                        </TemplatesLayout>
                      }
                    />
                    <Route
                      element={
                        <CanGuard permission={createTemplatePagePermissions} />
                      }
                    >
                      <Route
                        path="create"
                        element={
                          <MainLayout title="Створення макету" backTrigger>
                            <TemplateCreationPage />
                          </MainLayout>
                        }
                      />
                    </Route>
                    <Route
                      path="edit"
                      element={
                        <CanGuard permission={editTemplatesPagePermissions} />
                      }
                    >
                      <Route
                        index
                        element={
                          <TemplatesLayout backTrigger>
                            <EditTemplatesPage />
                          </TemplatesLayout>
                        }
                      />
                      <Route
                        path=":id"
                        element={
                          <EditTemplateLayout>
                            <EditTemplatePage />
                          </EditTemplateLayout>
                        }
                      />
                    </Route>
                    <Route
                      path=":id"
                      element={
                        <CanGuard permission={templatePagePermissions} />
                      }
                    >
                      <Route
                        index
                        element={
                          <TemplateLayout>
                            <TemplatePage />
                          </TemplateLayout>
                        }
                      />
                      <Route
                        path="add"
                        element={
                          <MainLayout title="Додати інгредієнт" backTrigger>
                            <AddTemplateIngredientPage />
                          </MainLayout>
                        }
                      />
                    </Route>
                  </Route>
                  <Route element={<EditIngredientGuard />}>
                    <Route
                      path=":id"
                      element={
                        <EditIngredientLayout>
                          <EditIngredientPage />
                        </EditIngredientLayout>
                      }
                    />
                  </Route>
                </Route>
              </Route>

              <Route
                element={<FeatureFlagGuard featureFlag="ffMealsV2" negative />}
              >
                <Route path="meal/:id">
                  <Route
                    index
                    element={
                      <MealLayoutV1 title="Прийом їжі">
                        <MealPageV1 />
                      </MealLayoutV1>
                    }
                  />
                  <Route element={<EditIngredientsGuardV1 />}>
                    <Route
                      path="ingredients-edit"
                      element={
                        <EditMealLayoutV1 title="Редагувати інгредієнти">
                          <IngredientsEditPageV1 />
                        </EditMealLayoutV1>
                      }
                    />
                    <Route element={<CreateIngredientsGuardV1 />}>
                      <Route
                        path="add-ingredient"
                        element={
                          <MainLayout
                            title="Додати інгредієнт"
                            backTrigger={{
                              mode: "strict",
                              to: "../ingredients-edit",
                            }}
                          >
                            <AddIngredientPageV1 />
                          </MainLayout>
                        }
                      />
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route
              path="/nutrition-edit"
              element={
                <FeatureFlagGuard featureFlag="ffMealsV2" negative>
                  <CanGuard permission={editNutritionPagePermissionsV1} />
                </FeatureFlagGuard>
              }
            >
              <Route
                index
                element={
                  <NutritionLayout backTrigger>
                    <NutritionEditPage />
                  </NutritionLayout>
                }
              />
              <Route
                path="templates"
                element={<CanGuard permission={templatesPagePermissionsV1} />}
              >
                <Route
                  index
                  element={
                    <NutritionEditTemplatesLayout>
                      <NutritionEditTemplatesPage />
                    </NutritionEditTemplatesLayout>
                  }
                />
                <Route
                  element={
                    <CanGuard permission={createTemplatePagePermissionsV1} />
                  }
                >
                  <Route
                    path="create"
                    element={
                      <MainLayout title="Створення макету" backTrigger>
                        <NutritionEditTemplatesCreatePage />
                      </MainLayout>
                    }
                  />
                </Route>
                <Route
                  path="edit"
                  element={
                    <CanGuard permission={editTemplatesPagePermissionsV1} />
                  }
                >
                  <Route
                    index
                    element={
                      <NutritionEditTemplatesLayout>
                        <NutritionEditTemplatesEditPage />
                      </NutritionEditTemplatesLayout>
                    }
                  />
                  <Route
                    path=":id"
                    element={
                      <NutritionEditEditTemplateLayout>
                        <NutritionEditTemplateEditPage />
                      </NutritionEditEditTemplateLayout>
                    }
                  />
                </Route>
                <Route
                  path=":id/meals"
                  element={
                    <CanGuard permission={templateMealsPagePermissionsV1} />
                  }
                >
                  <Route
                    index
                    element={
                      <NutritionEditTemplateMealsLayout>
                        <NutritionEditTemplateMealsPage />
                      </NutritionEditTemplateMealsLayout>
                    }
                  />
                  <Route
                    element={
                      <CanGuard
                        permission={editTemplateMealsPagePermissionsV1}
                      />
                    }
                  >
                    <Route
                      path="edit"
                      element={
                        <NutritionEditTemplateMealsLayout backTrigger>
                          <NutritionEditTemplateMealsEditPage />
                        </NutritionEditTemplateMealsLayout>
                      }
                    />
                  </Route>
                </Route>
              </Route>
            </Route>
            <Route path="/exercise">
              <Route
                index
                element={
                  <MainLayout title="Оберіть тип тренування" backTrigger>
                    <ExercisePage />
                  </MainLayout>
                }
              />
              <Route
                path="home"
                element={
                  <MainLayout title="Домашнє тренування" backTrigger>
                    <HomeExercisePage />
                  </MainLayout>
                }
              />
              <Route
                path="gym"
                element={
                  <MainLayout title="Тренування в залі" backTrigger>
                    <GymExercisePage />
                  </MainLayout>
                }
              />
            </Route>
            <Route path="/settings">
              <Route
                index
                element={
                  <Suspense fallback={<Loader />}>
                    <MainLayout title="Налаштування" backTrigger>
                      <SettingsPage />
                    </MainLayout>
                  </Suspense>
                }
              />
              <Route
                path="/settings/system"
                element={
                  <Suspense fallback={<Loader />}>
                    <MainLayout title="Системна інформація" backTrigger>
                      <SettingsLayout>
                        <SystemSettingsPage />
                      </SettingsLayout>
                    </MainLayout>
                  </Suspense>
                }
              />
              <Route
                path="/settings/profile"
                element={
                  <Suspense fallback={<Loader />}>
                    <MainLayout title="Основна інформація" backTrigger>
                      <SettingsLayout>
                        <ProfileSettingsPage />
                      </SettingsLayout>
                    </MainLayout>
                  </Suspense>
                }
              />
              <Route
                path="/settings/medical"
                element={
                  <Suspense fallback={<Loader />}>
                    <MainLayout title="Медична інформація" backTrigger>
                      <SettingsLayout>
                        <MedicalSettingsPage />
                      </SettingsLayout>
                    </MainLayout>
                  </Suspense>
                }
              />
              <Route
                path="/settings/activity"
                element={
                  <Suspense fallback={<Loader />}>
                    <MainLayout title="Активність та раціон" backTrigger>
                      <SettingsLayout>
                        <ActivitySettingsPage />
                      </SettingsLayout>
                    </MainLayout>
                  </Suspense>
                }
              />
              <Route path="/settings/billing">
                <Route
                  index
                  element={
                    <Suspense fallback={<Loader />}>
                      <BillingLayout>
                        <SettingsLayout>
                          <BillingSettingsPage />
                        </SettingsLayout>
                      </BillingLayout>
                    </Suspense>
                  }
                />
                <Route element={<CanGuard permission={viewBillingAll} />}>
                  <Route
                    path="/settings/billing/all"
                    element={
                      <Suspense fallback={<Loader />}>
                        <BillingAllPage />
                      </Suspense>
                    }
                  />
                </Route>
              </Route>
              <Route element={<CanGuard permission={usersPagePermissions} />}>
                <Route
                  path="/settings/users"
                  element={
                    <Suspense fallback={<Loader />}>
                      <MainLayout
                        title="Обрати користувача"
                        backTrigger
                        usersSwipe
                      >
                        <SettingsLayout>
                          <ChooseUsersPage />
                        </SettingsLayout>
                      </MainLayout>
                    </Suspense>
                  }
                />
              </Route>
            </Route>

            <Route element={<CanGuard permission={sharePagePermissions} />}>
              <Route path="/share" element={<SharePage />} />
            </Route>

            <Route
              path="/measurements"
              element={
                <MainLayout title="Заміри" backTrigger>
                  <MeasurementsPage />
                </MainLayout>
              }
            />
          </Route>

          <Route
            element={
              <Suspense fallback={<Loader />}>
                <ActiveSubscriptionGuard />
              </Suspense>
            }
          >
            <Route path="/welcome" element={<WelcomePage />} />
            <Route
              path="/subscription-expired"
              element={<SubscriptionExpiredPage />}
            />
          </Route>
        </Route>
        {/* Скрытая страница feature flags */}
        <Route
          path="/feature-flags"
          element={
            <MainLayout title="Feature Flags" backTrigger>
              <FeatureFlagsPage />
            </MainLayout>
          }
        />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

export default RoutingSystem;
